// src/pages/AssignDeliveries.js — Instation/Outstation tabs, standard card
// sizing, date range (defaults to Today). Team leader / owner selects a
// delivery person, then scans or clicks to select packed invoices to assign.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../utils/api';

const STATUS_STYLE = {
  stage_4_packing:    { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe', label: 'Ready for Delivery' },
  stage_5_dispatched: { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc', label: 'Dispatched' },
  stage_6_delivered:  { bg: '#dcfce7', text: '#166534', border: '#86efac', label: 'Delivered' },
  complete:            { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7', label: 'Complete' },
};

function statusStyle(status) {
  return STATUS_STYLE[status] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db', label: status?.replace(/_/g, ' ') || '—' };
}

function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today = () => localDateString(new Date());

const CARD_STYLE = {
  width: '150px',
  height: '100px',
  padding: '8px 12px',
  borderRadius: '10px',
  fontSize: '12px',
  fontFamily: 'monospace',
  fontWeight: 'bold',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  boxSizing: 'border-box',
  flexShrink: 0,
};

export default function AssignDeliveries() {
  const navigate = useNavigate();

  const [tab, setTab] = useState('instation');
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());

  const [persons, setPersons] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedInvs, setSelectedInvs] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState(null);
  // Invoices pending article-count entry before the actual assign call —
  // opened after clicking "Assign to X" instead of assigning immediately.
  const [articleCountQueue, setArticleCountQueue] = useState(null); // array of invoice objects, or null

  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeRef = useRef(null);
  const firstLoadDone = useRef(false);

  const load = useCallback(async () => {
    if (firstLoadDone.current) setRefreshing(true);
    else setLoading(true);
    try {
      const [pRes, dayRes, readyRes] = await Promise.all([
        get('/api/dispatch/delivery-persons'),
        // BILL DATE rule: the chosen dates pick bills by bill date and show where each one stands today
        get(`/api/invoices?limit=1000&offset=0&invoice_date_from=${dateFrom}&invoice_date_to=${dateTo}`),
        // every Ready for Delivery bill is always listed, whatever its date
        get(`/api/invoices?limit=1000&offset=0&status=stage_4_packing`),
      ]);
      setPersons(pRes.delivery_persons || []);
      const shownStatuses = ['stage_4_packing', 'stage_5_dispatched', 'stage_6_delivered', 'stage_7_returned_to_office', 'complete'];
      const merged = new Map();
      [...(dayRes.invoices || []).filter(i => shownStatuses.includes(i.status)), ...(readyRes.invoices || [])]
        .forEach(i => merged.set(i.invoice_id, i));
      setInvoices([...merged.values()]
        .filter(i => (i.delivery_type || 'unclassified') === tab)
        .sort((a, b) => String(b.invoice_number).localeCompare(String(a.invoice_number), undefined, { numeric: true })));
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
      firstLoadDone.current = true;
    }
  }, [tab, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setSelectedInvs(new Set());
  }, [tab]);

  useEffect(() => {
    if (selectedPerson && barcodeRef.current) barcodeRef.current.focus();
  }, [selectedPerson]);

  function toggleInvoice(invoiceId) {
    setSelectedInvs(prev => {
      const next = new Set(prev);
      next.has(invoiceId) ? next.delete(invoiceId) : next.add(invoiceId);
      return next;
    });
  }

  function handleBarcode(e) {
    e.preventDefault();
    const raw = barcodeInput.trim();
    if (!raw) return;
    setBarcodeInput('');
    if (barcodeRef.current) barcodeRef.current.focus();

    const match = invoices.find(i => i.invoice_number?.toLowerCase() === raw.toLowerCase());
    if (!match) {
      setMsg({ type: 'error', text: `Invoice "${raw}" not found in ${tab} list for this date range` });
      return;
    }
    const wasSelected = selectedInvs.has(match.invoice_id);
    setSelectedInvs(prev => {
      const next = new Set(prev);
      next.has(match.invoice_id) ? next.delete(match.invoice_id) : next.add(match.invoice_id);
      return next;
    });
    setMsg({ type: 'success', text: `${match.invoice_number} ${wasSelected ? 'deselected' : 'selected'} — ${match.party_name}` });
  }

  // Clicking "Assign to X" no longer assigns immediately — it opens the
  // article-count prompt first. The actual /assign calls happen in
  // handleAssign, invoked from the modal once counts are entered.
  function openArticleCountPrompt() {
    if (!selectedPerson || selectedInvs.size === 0) return;
    const queue = invoices.filter(i => selectedInvs.has(i.invoice_id));
    setArticleCountQueue(queue);
  }

  async function handleAssign(articleCounts) {
    if (!selectedPerson || selectedInvs.size === 0) return;
    setAssigning(true);
    setMsg(null);
    let successCount = 0;
    let errors = [];

    for (const invoiceId of selectedInvs) {
      try {
        await post(`/api/dispatch/assign/${invoiceId}`, {
          delivery_person_id: selectedPerson.id,
          article_count: articleCounts[invoiceId] != null ? articleCounts[invoiceId] : null,
        });
        successCount++;
      } catch (err) {
        const inv = invoices.find(i => i.invoice_id === invoiceId);
        errors.push(`${inv?.invoice_number || invoiceId}: ${err.message}`);
      }
    }

    setAssigning(false);
    setSelectedInvs(new Set());
    setArticleCountQueue(null);
    await load();

    if (errors.length === 0) {
      setMsg({ type: 'success', text: `${successCount} invoice${successCount !== 1 ? 's' : ''} assigned to ${selectedPerson.name}` });
    } else {
      setMsg({ type: 'error', text: `${successCount} assigned. Errors: ${errors.join('; ')}` });
    }
  }

  async function handleDeassign(inv) {
    if (!window.confirm(`De-assign ${inv.invoice_number}? It will go back to Ready for Delivery.`)) return;
    try {
      await post(`/api/dispatch/deassign/${inv.invoice_id}`, {});
      setMsg({ type: 'success', text: `${inv.invoice_number} de-assigned` });
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  const unpicked = invoices.filter(i => i.status === 'stage_4_packing');
  const picked = invoices.filter(i => i.status !== 'stage_4_packing');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px 16px' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <button onClick={() => navigate(-1)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1d4ed8', background: 'none', border: 'none', cursor: 'pointer' }}>
            ← Back
          </button>
          <button onClick={load}
            style={{ padding: '8px 12px', fontSize: '14px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '10px', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '52px', boxSizing: 'border-box', position: 'sticky', top: 0, zIndex: 9, backgroundColor: '#f3f4f6' }}>
          {[['instation', 'Instation'], ['outstation', 'Outstation'], ['unclassified', 'Not set']].map(([val, label]) => (
            <button key={val} onClick={() => setTab(val)}
              style={{
                padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                border: tab === val ? '2px solid #1e3a8a' : '2px solid #e5e7eb',
                backgroundColor: tab === val ? '#1e3a8a' : 'white', color: tab === val ? 'white' : '#4b5563',
                transition: 'all 0.2s',
              }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', backgroundColor: 'white', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e5e7eb', position: 'sticky', top: '52px', zIndex: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          </div>
          <button onClick={() => { setDateFrom(today()); setDateTo(today()); }}
            style={{ padding: '7px 14px', fontSize: '12px', fontWeight: '600', backgroundColor: '#eff6ff', color: '#1d4ed8', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
            Today
          </button>
        </div>

        {msg && (
          <div style={{
            marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '500',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            backgroundColor: msg.type === 'success' ? '#f0fdf4' : '#fef2f2',
            color: msg.type === 'success' ? '#15803d' : '#b91c1c',
            border: `1px solid ${msg.type === 'success' ? '#86efac' : '#fecaca'}`,
          }}>
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} style={{ marginLeft: '12px', opacity: 0.6, background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>×</button>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
            <div style={{ width: '32px', height: '32px', border: '4px solid #bfdbfe', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>

            <div style={{ width: '260px', flexShrink: 0 }}>
              <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', margin: 0 }}>Delivery Persons</p>
                  <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0' }}>{persons.length} active today</p>
                </div>
                {persons.length === 0 ? (
                  <p style={{ padding: '24px 16px', fontSize: '14px', color: '#9ca3af', textAlign: 'center' }}>No delivery persons available</p>
                ) : persons.map(p => {
                  const sel = selectedPerson?.id === p.id;
                  return (
                    <button key={p.id}
                      onClick={() => { setSelectedPerson(p); setSelectedInvs(new Set()); setMsg(null); }}
                      style={{
                        width: '100%', textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
                        display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', border: 'none',
                        backgroundColor: sel ? '#1e3a8a' : 'white', color: sel ? 'white' : '#1f2937',
                      }}>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', fontSize: '14px', flexShrink: 0,
                        backgroundColor: sel ? '#1d4ed8' : '#dbeafe', color: sel ? 'white' : '#1d4ed8',
                      }}>
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontWeight: '600', fontSize: '14px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</p>
                        <p style={{ fontSize: '12px', margin: '2px 0 0 0', color: sel ? '#93c5fd' : '#9ca3af' }}>{p.phone}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: '320px' }}>
              {!selectedPerson ? (
                <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '2px dashed #e5e7eb', height: '256px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    
                    <p style={{ color: '#6b7280', fontWeight: '600', margin: 0 }}>Select a delivery person</p>
                    <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>to assign invoices</p>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '256px' }}>
                        <p style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563', marginBottom: '6px' }}>
                          Scan barcode or click invoice blocks to select ({tab})
                        </p>
                        <form onSubmit={handleBarcode}>
                          <input
                            ref={barcodeRef}
                            type="text"
                            value={barcodeInput}
                            onChange={e => setBarcodeInput(e.target.value)}
                            placeholder="Scan invoice barcode..."
                            autoComplete="off"
                            style={{ width: '100%', padding: '10px 16px', border: '2px solid #fcd34d', borderRadius: '10px', fontSize: '14px', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </form>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e3a8a', margin: 0 }}>{selectedInvs.size}</p>
                          <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>selected</p>
                        </div>
                        <button onClick={openArticleCountPrompt} disabled={selectedInvs.size === 0 || assigning}
                          style={{
                            padding: '10px 24px', backgroundColor: '#1e3a8a', color: 'white', borderRadius: '10px', fontWeight: 'bold',
                            fontSize: '14px', border: 'none', cursor: selectedInvs.size === 0 || assigning ? 'default' : 'pointer',
                            opacity: selectedInvs.size === 0 || assigning ? 0.5 : 1,
                          }}>
                          {assigning ? 'Assigning...' : `Assign to ${selectedPerson.name.split(' ')[0]}`}
                        </button>
                        {selectedInvs.size > 0 && (
                          <button onClick={() => setSelectedInvs(new Set())}
                            style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', color: '#6b7280', backgroundColor: 'white', cursor: 'pointer' }}>
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {[
                      { color: '#f3e8ff', border: '#d8b4fe', label: 'Packed — ready to assign' },
                      { color: '#e0e7ff', border: '#a5b4fc', label: 'Dispatched' },
                      { color: '#dcfce7', border: '#86efac', label: 'Delivered' },
                      { color: '#1e3a8a', border: '#1e3a8a', label: 'Selected' },
                    ].map(l => (
                      <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '16px', height: '16px', borderRadius: '4px', backgroundColor: l.color, border: `1px solid ${l.border}`, display: 'inline-block' }} />
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{l.label}</span>
                      </div>
                    ))}
                  </div>

                  {unpicked.length > 0 && (
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <p style={{ fontSize: '14px', fontWeight: '600', color: '#6b21a8', margin: 0 }}>Packed — Ready to Assign</p>
                          <p style={{ fontSize: '12px', color: '#7c3aed', margin: '2px 0 0 0' }}>{unpicked.length} invoice{unpicked.length !== 1 ? 's' : ''} available</p>
                        </div>
                        <button
                          onClick={() => {
                            const allSelected = unpicked.every(i => selectedInvs.has(i.invoice_id));
                            if (allSelected) {
                              setSelectedInvs(prev => {
                                const next = new Set(prev);
                                unpicked.forEach(i => next.delete(i.invoice_id));
                                return next;
                              });
                            } else {
                              setSelectedInvs(prev => new Set([...prev, ...unpicked.map(i => i.invoice_id)]));
                            }
                          }}
                          style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f3e8ff', color: '#6b21a8', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                          {unpicked.every(i => selectedInvs.has(i.invoice_id)) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {unpicked.map(inv => {
                            const sel = selectedInvs.has(inv.invoice_id);
                            return (
                              <button key={inv.invoice_id} onClick={() => toggleInvoice(inv.invoice_id)}
                                title={`${inv.party_name} · ${inv.city} · ₹${Number(inv.invoice_value).toLocaleString('en-IN')}`}
                                style={{
                                  ...CARD_STYLE,
                                  border: `2px solid ${sel ? '#1e3a8a' : '#d8b4fe'}`,
                                  backgroundColor: sel ? '#1e3a8a' : '#f3e8ff', color: sel ? 'white' : '#6b21a8',
                                  transform: sel ? 'scale(1.03)' : 'none',
                                }}>
                                <p style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.invoice_number}</p>
                                <p style={{ fontSize: '11px', fontWeight: 'normal', margin: '4px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: sel ? '#bfdbfe' : '#7c3aed' }}>
                                  {inv.party_name}
                                </p>
                                <p style={{ fontSize: '10px', fontWeight: 'normal', margin: '2px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: sel ? '#93c5fd' : '#a78bfa' }}>
                                  {inv.city || '—'}
                                </p>
                                {sel && <p style={{ fontSize: '10px', margin: '2px 0 0 0', color: '#bfdbfe' }}>✓ Selected</p>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {picked.length > 0 && (
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>Already Dispatched / In Progress</p>
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>{picked.length} invoices</p>
                      </div>
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {picked.map(inv => {
                            const st = statusStyle(inv.status);
                            const canDeassign = inv.status === 'stage_5_dispatched';
                            return (
                              <div key={inv.invoice_id} title={`${inv.party_name} · ${inv.city} · ${st.label}`}
                                style={{ ...CARD_STYLE, cursor: 'default', border: `1px solid ${st.border}`, backgroundColor: st.bg, color: st.text, height: canDeassign ? '118px' : '100px' }}>
                                <p style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.invoice_number}</p>
                                <p style={{ fontSize: '11px', fontWeight: 'normal', margin: '4px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.7 }}>
                                  {inv.party_name}
                                </p>
                                <p style={{ fontSize: '10px', fontWeight: 'normal', margin: '2px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.6 }}>
                                  {inv.city || '—'}
                                </p>
                                <p style={{ fontSize: '10px', margin: '2px 0 0 0', opacity: 0.6 }}>{st.label}</p>
                                {canDeassign && (
                                  <button
                                    onClick={() => handleDeassign(inv)}
                                    style={{ marginTop: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.6)', color: st.text, border: `1px solid ${st.border}`, borderRadius: '6px', cursor: 'pointer', alignSelf: 'flex-start' }}
                                  >
                                    De-assign
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {unpicked.length === 0 && picked.length === 0 && (
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '2px dashed #e5e7eb', padding: '48px', textAlign: 'center' }}>
                      
                      <p style={{ color: '#6b7280', fontWeight: '600', margin: 0 }}>No {tab} invoices found for this date range</p>
                      <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>Try widening the date range or check back after packing completes</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {articleCountQueue && (
        <ArticleCountModal
          invoices={articleCountQueue}
          assigning={assigning}
          onCancel={() => setArticleCountQueue(null)}
          onConfirm={(counts) => handleAssign(counts)}
        />
      )}
    </div>
  );
}

// Asks for the number of articles per invoice after multi-select, before
// the actual assign calls fire. Defaults every field to 1 so a delivery
// person can't be assigned zero-article invoices by accident, but each
// field is fully editable.
function ArticleCountModal({ invoices, assigning, onCancel, onConfirm }) {
  const [counts, setCounts] = useState(() =>
    Object.fromEntries(invoices.map(i => [i.invoice_id, 1]))
  );

  function setCount(invoiceId, val) {
    setCounts(prev => ({ ...prev, [invoiceId]: val }));
  }

  function confirm() {
    const parsed = {};
    for (const inv of invoices) {
      const n = parseInt(counts[inv.invoice_id], 10);
      parsed[inv.invoice_id] = isNaN(n) || n < 0 ? 0 : n;
    }
    onConfirm(parsed);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '14px', width: '100%', maxWidth: '440px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontWeight: '700', fontSize: '16px', margin: 0, color: '#1f2937' }}>Number of Articles</p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''} selected</p>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9ca3af', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {invoices.map(inv => (
            <div key={inv.invoice_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontWeight: '600', fontSize: '14px', margin: 0, fontFamily: 'monospace', color: '#1f2937' }}>{inv.invoice_number}</p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.party_name}</p>
              </div>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={counts[inv.invoice_id]}
                onChange={e => setCount(inv.invoice_id, e.target.value)}
                style={{ width: '64px', padding: '8px 10px', border: '2px solid #c4b5fd', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', outline: 'none', flexShrink: 0 }}
              />
            </div>
          ))}
        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px' }}>
          <button onClick={onCancel} disabled={assigning}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#6b7280', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={confirm} disabled={assigning}
            style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#1e3a8a', color: 'white', fontWeight: '700', fontSize: '14px', cursor: assigning ? 'default' : 'pointer', opacity: assigning ? 0.6 : 1 }}>
            {assigning ? 'Assigning...' : 'Confirm & Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}
